/**
 * ---------------------------------------------------------------------------
 * "להשלמת החוויה" slider
 *
 * Behaviour for sections/fbt-slider.liquid - the native replacement for the
 * Variant experience that rebuilt the frequently-bought-together app widget as
 * a card slider.
 *
 * Only what cannot be rendered on the server is here. The cards, their
 * variants, their prices and the add-to-cart forms come from Liquid; adding to
 * the cart is the theme's own <product-form>, which bundles the drawer and the
 * header into the request and opens the drawer afterwards. That leaves four
 * things:
 *
 *   - swapping the displayed price when a variant is picked, and pointing the
 *     form's hidden id input at it
 *   - drag-to-scroll with the mouse
 *   - the pagination dots
 *   - the checkmark a card shows before it slides away, once the theme reports
 *     the line was added (the "variant:add" event the product form dispatches)
 *
 * The original also re-implemented the add itself: a bare FormData POST, a
 * cart:refresh event, and a click on #cart-icon-bubble 1200ms later to open the
 * drawer - which it fired before the request had even been sent, so the card
 * disappeared whether or not the line was actually added. Here the card only
 * leaves after the theme confirms it.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var CHECKMARK_HOLD_MS = 1000;
  var CARD_EXIT_MS = 800;

  function money(option) {
    return option.getAttribute('data-price') || '';
  }

  var FbtSlider = class extends HTMLElement {
    connectedCallback() {
      if (this.initialised) {
        return;
      }

      this.initialised = true;
      this.scroller = this.querySelector('[data-fbt-scroller]');
      this.dotsContainer = this.querySelector('[data-fbt-dots]');

      this.addEventListener('change', this.onVariantChange.bind(this));
      this.addEventListener('variant:add', this.onVariantAdded.bind(this));

      this.setupDragScroll();
      this.setupDots();
    }

    // -----------------------------------------------------------------------
    // Variants
    // -----------------------------------------------------------------------
    onVariantChange(event) {
      var select = event.target.closest('[data-fbt-variant-select]');

      if (!select) {
        return;
      }

      var card = select.closest('[data-fbt-card]');
      var option = select.options[select.selectedIndex];
      var price = card.querySelector('[data-fbt-price]');
      var input = card.querySelector('[data-fbt-variant-input]');

      if (price) {
        price.innerHTML = money(option);
      }

      if (input) {
        input.value = select.value;
      }
    }

    // -----------------------------------------------------------------------
    // Added to cart
    // -----------------------------------------------------------------------
    onVariantAdded(event) {
      var card = event.target.closest('[data-fbt-card]');

      if (!card) {
        return;
      }

      this.showCheckmark(card);
    }

    showCheckmark(card) {
      var overlay = document.createElement('div');

      overlay.className = 'variant-card-success-overlay';
      overlay.innerHTML =
        '<div class="variant-card-success-checkmark">' +
        '<svg viewBox="0 0 52 52" aria-hidden="true" focusable="false"><path d="M14 27l7.5 7.5L38 18"/></svg>' +
        '</div>';

      card.appendChild(overlay);

      setTimeout(
        function () {
          card.classList.add('added-to-cart');

          setTimeout(
            function () {
              card.remove();
              this.setupDots();
            }.bind(this),
            CARD_EXIT_MS
          );
        }.bind(this),
        CHECKMARK_HOLD_MS
      );
    }

    // -----------------------------------------------------------------------
    // Dragging
    //
    // Touch scrolling is the browser's; this is only for the mouse.
    // -----------------------------------------------------------------------
    setupDragScroll() {
      var scroller = this.scroller;

      if (!scroller) {
        return;
      }

      var isDown = false;
      var startX = 0;
      var startScrollLeft = 0;

      scroller.addEventListener('mousedown', function (event) {
        isDown = true;
        startX = event.pageX;
        startScrollLeft = scroller.scrollLeft;
        scroller.classList.add('is-dragging');
      });

      ['mouseleave', 'mouseup'].forEach(function (name) {
        scroller.addEventListener(name, function () {
          isDown = false;
          scroller.classList.remove('is-dragging');
        });
      });

      scroller.addEventListener('mousemove', function (event) {
        if (!isDown) {
          return;
        }

        event.preventDefault();
        scroller.scrollLeft = startScrollLeft - (event.pageX - startX) * 2;
      });
    }

    // -----------------------------------------------------------------------
    // Dots
    //
    // One dot per screenful, which is what the row actually scrolls by. The
    // original capped the count at four and then multiplied a hardcoded card
    // width by Math.ceil(products / dots) to guess where each dot should land,
    // so the last dot rarely matched the end of the row.
    // -----------------------------------------------------------------------
    setupDots() {
      var scroller = this.scroller;
      var dotsContainer = this.dotsContainer;

      if (!scroller || !dotsContainer) {
        return;
      }

      var pages = Math.ceil(scroller.scrollWidth / scroller.clientWidth);

      // The store runs right to left, where scrollLeft counts down from zero.
      var direction = window.getComputedStyle(scroller).direction === 'rtl' ? -1 : 1;

      dotsContainer.innerHTML = '';

      if (pages < 2) {
        return;
      }

      for (var index = 0; index < pages; index++) {
        var dot = document.createElement('button');

        dot.type = 'button';
        dot.className = 'variant-slider-dot';
        dot.setAttribute('aria-label', String(index + 1));

        if (index === 0) {
          dot.classList.add('active');
        }

        dot.addEventListener(
          'click',
          function (page) {
            scroller.scrollTo({ left: direction * page * scroller.clientWidth, behavior: 'smooth' });
          }.bind(null, index)
        );

        dotsContainer.appendChild(dot);
      }

      if (!this.scrollBound) {
        this.scrollBound = true;

        scroller.addEventListener('scroll', function () {
          var active = Math.round(Math.abs(scroller.scrollLeft) / scroller.clientWidth);

          Array.prototype.forEach.call(dotsContainer.children, function (dot, index) {
            dot.classList.toggle('active', index === active);
          });
        });
      }
    }
  };

  if (!window.customElements.get('fbt-slider')) {
    window.customElements.define('fbt-slider', FbtSlider);
  }
})();
